import React, { useState } from 'react';
import GameEntry from './GameEntry.jsx';
import Button from '../common/Button.jsx';
import Loading from '../common/Loading.jsx';
import ErrorMessage from '../common/ErrorMessage.jsx';
import './MatchLogger.css';

/**
 * Component for logging complete match results with game-by-game details
 * @param {Object} props
 * @param {Object} props.match - Match object
 * @param {Array} props.team1Players - Team 1 player roster
 * @param {Array} props.team2Players - Team 2 player roster
 * @param {number} props.gamesPerMatch - Number of games in the match (default 7)
 * @param {Function} props.onSubmit - Callback when results are submitted
 * @param {Function} props.onCancel - Callback when cancelled
 */
export default function MatchLogger({
  match,
  team1Players,
  team2Players,
  gamesPerMatch = 7,
  onSubmit,
  onCancel,
}) {
  const [games, setGames] = useState(() =>
    Array.from({ length: gamesPerMatch }, (_, i) => ({
      gameNumber: i + 1,
      team1PlayerId: '',
      team2PlayerId: '',
      lagWinner: '',
      breaker: '',
      winner: '',
    }))
  );
  
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Calculate scores from game winners
  const team1Score = games.filter((g) => g.winner === 'team1').length;
  const team2Score = games.filter((g) => g.winner === 'team2').length;

  const handleGameChange = (gameNumber, updatedGame) => {
    setGames((prev) =>
      prev.map((g) => (g.gameNumber === gameNumber ? updatedGame : g))
    );
  };

  const validateGames = () => {
    for (const game of games) {
      if (
        !game.team1PlayerId ||
        !game.team2PlayerId ||
        !game.lagWinner ||
        !game.breaker ||
        !game.winner
      ) {
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateGames()) {
      setError('Please complete all game details before submitting');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      await onSubmit({
        games,
        team1Score,
        team2Score,
      });
    } catch (err) {
      setError(err.message || 'Failed to submit match results');
      setSubmitting(false);
    }
  };

  return (
    <div className="match-logger">
      <div className="match-logger-header">
        <h3>Log Match Results</h3>
        <div className="match-info">
          <span className="team-name">{match.team1Name}</span>
          <span className="vs">vs</span>
          <span className="team-name">{match.team2Name}</span>
        </div>
      </div>

      <div className="score-summary">
        <div className="score-box team1">
          <div className="team-label">Team 1</div>
          <div className="score">{team1Score}</div>
        </div>
        <div className="score-divider">-</div>
        <div className="score-box team2">
          <div className="team-label">Team 2</div>
          <div className="score">{team2Score}</div>
        </div>
      </div>

      {error && <ErrorMessage message={error} />}

      <form onSubmit={handleSubmit}>
        <div className="games-list">
          {games.map((game) => (
            <GameEntry
              key={game.gameNumber}
              gameNumber={game.gameNumber}
              game={game}
              onChange={(updatedGame) =>
                handleGameChange(game.gameNumber, updatedGame)
              }
              team1Players={team1Players}
              team2Players={team2Players}
            />
          ))}
        </div>

        <div className="actions">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting || !validateGames()}
          >
            {submitting ? <Loading size="small" /> : 'Submit Results'}
          </Button>
        </div>
      </form>
    </div>
  );
}
